import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  FileText, Plus, CheckCircle, Clock, Upload, Download, 
  PenTool, User, ChevronRight, ArrowLeft, AlertTriangle, Eye
} from 'lucide-react';

const DOC_TYPE_ICONS = {
  house_rules: FileText,
  liability_waiver: AlertTriangle,
  release_of_info: FileText,
  program_agreement: FileText,
  medication_policy: FileText,
  resident_handbook: FileText,
  rental_agreement: FileText,
  emergency_contact: User,
  intake_form: FileText,
  photo_release: FileText,
  background_check: FileText,
  other: FileText,
};

export default function HousingDocumentsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [residents, setResidents] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [housesRes, typesRes, pendingRes] = await Promise.all([
        api.get('/housing/entity/our_square/houses'),
        api.get('/housing-documents/types'),
        api.get('/housing-documents/pending-signatures?entity=our_square'),
      ]);
      const allResidents = housesRes.data.flatMap(h => 
        (h.residents || []).map(r => ({ ...r, house_name: h.name }))
      );
      setResidents(allResidents);
      setDocTypes(typesRes.data);
      setPendingSignatures(pendingRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (selectedResident) {
    return <ResidentDocuments residentId={selectedResident.id} residentName={selectedResident.user_name} api={api} docTypes={docTypes} onBack={() => { setSelectedResident(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="housing-documents-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Documents & Agreements</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage housing documents, agreements, and digital signatures</p>
      </div>

      {/* Overview Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center mb-2"><User size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{residents.length}</p>
            <p className="text-xs text-muted-foreground">Active Residents</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2"><Clock size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{pendingSignatures.length}</p>
            <p className="text-xs text-muted-foreground">Pending Signatures</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center mb-2"><FileText size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{docTypes.filter(d => d.requires_signature).length}</p>
            <p className="text-xs text-muted-foreground">Required Documents</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Signatures Alert */}
      {pendingSignatures.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-amber-400">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PenTool size={16} className="text-amber-600" /> Documents Pending Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingSignatures.slice(0, 5).map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{d.resident_name}</p>
                      <p className="text-xs text-amber-700">{d.document_type_name}</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Awaiting Signature</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Residents List */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Residents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {residents.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => setSelectedResident(r)} data-testid={`resident-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 text-sm font-bold">
                    {r.user_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{r.user_name}</p>
                    <p className="text-xs text-muted-foreground">{r.house_name}</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </div>
            ))}
            {residents.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No active residents</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResidentDocuments({ residentId, residentName, api, docTypes, onBack }) {
  const [data, setData] = useState(null);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docForm, setDocForm] = useState({ document_type: '', title: '', notes: '' });
  const [signForm, setSignForm] = useState({ signature_text: '', signature_date: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/housing-documents/resident/${residentId}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, residentId]);

  useEffect(() => { load(); }, [load]);

  const createDocument = async () => {
    if (!docForm.document_type) return toast.error('Select document type');
    try {
      await api.post('/housing-documents', { resident_id: residentId, ...docForm });
      toast.success('Document created!');
      setNewDocOpen(false);
      setDocForm({ document_type: '', title: '', notes: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const signDocument = async () => {
    if (!signForm.signature_text) return toast.error('Enter your full name as signature');
    try {
      await api.post(`/housing-documents/${selectedDoc.id}/sign`, signForm);
      toast.success('Document signed!');
      setSignOpen(false);
      setSelectedDoc(null);
      setSignForm({ signature_text: '', signature_date: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openSign = (doc) => {
    setSelectedDoc(doc);
    setSignForm({ signature_text: '', signature_date: new Date().toISOString().split('T')[0] });
    setSignOpen(true);
  };

  if (loading || !data) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  const signedCount = data.total_signed || 0;
  const requiredCount = data.total_required || 0;
  const progress = requiredCount > 0 ? Math.round((signedCount / requiredCount) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="resident-documents">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="rounded-full"><ArrowLeft size={16} /></Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{residentName}</h2>
            <p className="text-xs text-muted-foreground">Documents & Agreements</p>
          </div>
        </div>
        <Dialog open={newDocOpen} onOpenChange={setNewDocOpen}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2"><Plus size={16} /> Add Document</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Add Document</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label>Document Type *</Label>
                <Select value={docForm.document_type} onValueChange={v => setDocForm({ ...docForm, document_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {docTypes.map(t => <SelectItem key={t.key} value={t.key}>{t.name} {t.requires_signature && '(Requires Signature)'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title (optional)</Label><Input value={docForm.title} onChange={e => setDocForm({ ...docForm, title: e.target.value })} /></div>
              <div><Label>Notes</Label><DictationInput value={docForm.notes} onChange={e => setDocForm({ ...docForm, notes: e.target.value })} rows={2} showCleanup={false} /></div>
              <Button onClick={createDocument} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Create Document</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Completion Progress */}
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-cyan-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-3xl font-bold text-cyan-700">{progress}%</p>
              <p className="text-sm text-muted-foreground">Documents Complete</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{signedCount}/{requiredCount}</p>
              <p className="text-sm text-muted-foreground">Required Signed</p>
            </div>
          </div>
          <Progress value={progress} className="h-3" />
        </CardContent>
      </Card>

      {/* Document Checklist */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Document Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.completion_status.map(item => {
              const Icon = DOC_TYPE_ICONS[item.document_type] || FileText;
              const isSigned = item.status === 'signed';
              
              return (
                <div key={item.document_type} className={`flex items-center justify-between p-3 rounded-lg ${isSigned ? 'bg-emerald-50' : item.requires_signature ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSigned ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {isSigned ? <CheckCircle size={16} /> : <Icon size={16} />}
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${isSigned ? 'text-emerald-700' : ''}`}>{item.name}</p>
                      {item.requires_signature && !isSigned && <p className="text-xs text-amber-600">Signature required</p>}
                    </div>
                  </div>
                  {isSigned ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Signed</Badge>
                  ) : (
                    <Badge className={item.requires_signature ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                      {item.requires_signature ? 'Pending' : 'Optional'}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* All Documents */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Documents ({data.documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <FileText size={20} className={doc.status === 'signed' ? 'text-emerald-600' : 'text-slate-400'} />
                  <div>
                    <p className="font-medium text-sm">{doc.title || doc.document_type_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(doc.created_at).toLocaleDateString()}
                      {doc.signed_at && ` • Signed ${new Date(doc.signed_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.status === 'signed' ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Signed by {doc.signed_by_name}</Badge>
                  ) : doc.requires_signature ? (
                    <Button size="sm" onClick={() => openSign(doc)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white gap-1">
                      <PenTool size={12} /> Sign
                    </Button>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">No signature needed</Badge>
                  )}
                </div>
              </div>
            ))}
            {data.documents.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No documents yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sign Dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Sign Document</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-4 bg-cyan-50 rounded-lg">
              <p className="font-medium text-cyan-800">{selectedDoc?.title || selectedDoc?.document_type_name}</p>
              <p className="text-xs text-cyan-600 mt-1">By signing, you acknowledge that you have read and agree to the terms of this document.</p>
            </div>
            <div>
              <Label>Full Legal Name (as signature) *</Label>
              <Input value={signForm.signature_text} onChange={e => setSignForm({ ...signForm, signature_text: e.target.value })} placeholder="Type your full legal name" className="mt-1" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={signForm.signature_date} onChange={e => setSignForm({ ...signForm, signature_date: e.target.value })} className="mt-1" />
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              <p className="font-medium">Electronic Signature Agreement</p>
              <p className="mt-1">By clicking "Sign Document", I agree that my typed name above constitutes my electronic signature and has the same legal effect as a handwritten signature.</p>
            </div>
            <Button onClick={signDocument} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2">
              <PenTool size={14} /> Sign Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
